import React, { useEffect, useMemo, useState } from "react";
import DataTable, { ColumnDef } from "@/components/utils/DataTable";
import { pageStyles } from "@/components/utils/PageLayout";
import { Button, FilterMultiSelect, FilterOption, signedClass, loadingStats } from "@/components/ui";
import type { StatItem } from "@/components/ui";
import { DateRangeFilter } from "@/components/utils/filters";
import { distributorContributionAPI } from "@/services/api";
import { CurrencyCode, formatCurrency, formatDate } from "@/utils/formatters";
import { sumBy } from "@/utils/aggregate";

type SchemeData = {
  scheme_id: number;
  scheme_name: string;
};

type ContributionData = {
  intermediary_name: string;
  capital_in: number;
  capital_out: number;
  net_capital: number;
};

type DistributorContributionResponse = {
  data: ContributionData[];
  scheme_data: SchemeData[];
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const fmt = (amount: number): string => formatCurrency(amount, CurrencyCode.INR, 0);

const monthStart = (): string => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 2).toISOString().split("T")[0];
};

const today = (): string => new Date().toISOString().split("T")[0];

const DistributorContribution: React.FC = () => {
  const [data, setData] = useState<DistributorContributionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedSchemes, setSelectedSchemes] = useState<FilterOption[]>([]);
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  // The range the rows on screen actually cover — set on a completed fetch, so
  // changing a date input does not relabel data that has not been reloaded.
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<keyof ContributionData>("net_capital");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof distributorContributionAPI.getDistributorContribution>[0] = {};
      if (selectedSchemes.length > 0) params.scheme_ids = selectedSchemes.map(o => Number(o.value));
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await distributorContributionAPI.getDistributorContribution(params);

      const uniqueSchemes = [...new Map(
        (Array.isArray(response.scheme_data) ? response.scheme_data : []).map((s: SchemeData) => [s.scheme_id, s] as const)
      ).values()] as SchemeData[];
      uniqueSchemes.sort((a, b) => a.scheme_name.localeCompare(b.scheme_name));

      setData({ ...response, scheme_data: uniqueSchemes });
      setAppliedRange({ from: fromDate, to: toDate });
      setCurrentPage(1);
    } catch (err) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(apiErr?.response?.data?.error || apiErr?.message || "Failed to fetch distributor contribution data.");
      setData(null);
    } finally {
      setHasFetched(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (field: string) => {
    const f = field as keyof ContributionData;
    if (sortBy === f) {
      setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(f);
      setSortDir("desc");
    }
    setCurrentPage(1);
  };

  const schemeOptions: FilterOption[] = useMemo(
    () => (data?.scheme_data ?? []).map(s => ({
      value: s.scheme_id.toString(),
      label: `${s.scheme_name} (ID: ${s.scheme_id})`,
    })),
    [data],
  );

  const filteredAndSorted = useMemo(() => {
    if (!data) return [] as ContributionData[];
    const query = searchTerm.toLowerCase();

    const result = data.data.filter(item => item.intermediary_name.toLowerCase().includes(query));

    return [...result].sort((a, b) => {
      let valA: any = a[sortBy];
      let valB: any = b[sortBy];

      if (sortBy === "intermediary_name") {
        valA = String(valA ?? "").toLowerCase();
        valB = String(valB ?? "").toLowerCase();
      } else {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
      }

      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, searchTerm, sortBy, sortDir]);

  const totalCount = filteredAndSorted.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedData = useMemo(
    () => filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredAndSorted, currentPage, pageSize],
  );

  // Mirrors the on-screen column order. The export carries data rows only — a
  // totals row would be double-counted by anything that sums the file.
  const csvColumns: { header: string; value: (r: ContributionData) => string | number }[] = [
    { header: "Intermediary Name", value: r => r.intermediary_name },
    { header: "Capital In", value: r => r.capital_in },
    { header: "Capital Out", value: r => r.capital_out },
    { header: "Net Capital", value: r => r.net_capital },
  ];

  const downloadCSV = () => {
    if (!filteredAndSorted.length) return;
    const esc = (v: string | number) =>
      typeof v === "number" ? String(v) : `"${v.replace(/"/g, '""')}"`;
    const csv = [
      csvColumns.map(c => esc(c.header)).join(","),
      ...filteredAndSorted.map(r => csvColumns.map(c => esc(c.value(r))).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `distributor_contribution_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  /** Net capital is a flow: an inflow reads positive, an outflow negative. */
  const signed = (value: number) => (
    <span className={signedClass(value) || undefined} style={{ fontWeight: 600 }}>
      {fmt(value)}
    </span>
  );

  const columns: ColumnDef<ContributionData>[] = [
    {
      key: "intermediary_name",
      header: "Intermediary Name",
      sortable: true,
      render: row => <span className={pageStyles['cell-strong']}>{row.intermediary_name}</span>,
      renderFooter: rows => (
        <>
          Totals{" "}
          <span className={pageStyles['cell-sub']}>
            ({rows.length} intermediar{rows.length === 1 ? "y" : "ies"}, all pages)
          </span>
        </>
      ),
    },
    {
      key: "capital_in",
      header: "Capital In (₹)",
      sortable: true,
      align: "right",
      render: row => fmt(row.capital_in),
      renderFooter: rows => fmt(sumBy(rows, "capital_in")),
    },
    {
      key: "capital_out",
      header: "Capital Out (₹)",
      sortable: true,
      align: "right",
      render: row => fmt(row.capital_out),
      renderFooter: rows => fmt(sumBy(rows, "capital_out")),
    },
    {
      key: "net_capital",
      header: "Net Capital (₹)",
      sortable: true,
      align: "right",
      render: row => signed(row.net_capital),
      renderFooter: rows => signed(sumBy(rows, "net_capital")),
    },
  ];

  // Summary over every filtered row, not just the visible page.
  const stats: StatItem[] | undefined = loading
    ? loadingStats(["Total Capital In", "Total Capital Out", "Total Net Capital"])
    : filteredAndSorted.length > 0 ? [
    { label: "Total Capital In", value: fmt(sumBy(filteredAndSorted, "capital_in")) },
    { label: "Total Capital Out", value: fmt(sumBy(filteredAndSorted, "capital_out")) },
    { label: "Total Net Capital", value: signed(sumBy(filteredAndSorted, "net_capital")) },
  ] : undefined;

  const filterSlot = (
    <>
      <FilterMultiSelect
        label="Scheme"
        value={selectedSchemes}
        onChange={setSelectedSchemes}
        options={schemeOptions}
      />

      <DateRangeFilter
        startDate={fromDate}
        endDate={toDate}
        onStartDateChange={setFromDate}
        onEndDateChange={setToDate}
        startId="dc-from-date"
        endId="dc-to-date"
      />

      <div className={pageStyles['filter-action']}>
        <Button variant="primary" onClick={fetchData} disabled={loading}>
          {loading ? "Loading…" : "Search"}
        </Button>
      </div>
    </>
  );

  return (
    <DataTable
      title="Distributor Contribution"
      subtitle={appliedRange
        ? `Capital raised and redeemed per intermediary from ${formatDate(appliedRange.from)} to ${formatDate(appliedRange.to)}`
        : undefined}
      stats={stats}
      data={paginatedData}
      footerRows={filteredAndSorted}
      columns={columns}
      rowKey={(row, index) => `${row.intermediary_name}-${index}`}
      totalCount={totalCount}
      loading={loading}
      loadingMessage="Loading distributor contributions…"
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
      sortField={sortBy}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={searchTerm}
      onSearchChange={value => { setSearchTerm(value); setCurrentPage(1); }}
      searchPlaceholder="Search by intermediary name…"
      filterSlot={filterSlot}
      activeFilterCount={selectedSchemes.length > 0 ? 1 : 0}
      onClearFilters={() => setSelectedSchemes([])}
      onDownloadCSV={downloadCSV}
      emptyMessage={hasFetched
        ? "No contributions found for the selected filters."
        : "Select filters and click Search to view distributor contributions."}
      fitContent
    />
  );
};

export default DistributorContribution;
